<template>
    <DSInput
        v-if="['text', 'password', 'email', 'number'].includes(type)"
        :id="fieldId"
        :type="type"
        :name="name"
        :required="required"
        :model-value="form.state[name]"
        :error="error"
        v-bind="$attrs"
        @submit="form.submit"
        @update:model-value="commitValue"
        @blur="form.validateField(name, { forceErrors: true })"
    />
    <DSTextarea
        v-else-if="['textarea'].includes(type)"
        :id="fieldId"
        :name="name"
        :model-value="form.state[name]"
        :error="error"
        v-bind="$attrs"
        @update:model-value="commitValue"
        @blur="form.validateField(name, { forceErrors: true })"
    />
    <DSCombobox
        v-else-if="['combobox', 'select'].includes(type)"
        :id="fieldId"
        :name="name"
        :model-value="form.state[name]"
        :error="error"
        v-bind="$attrs"
        @update:model-value="commitValue"
    />
    <DSSwitches
        v-else-if="['checkbox', 'switch', 'radio'].includes(type)"
        :type="type"
        :name="name"
        :model-value="form.state[name]"
        :error="error"
        v-bind="$attrs"
        @update:model-value="commitValue"
    >
        <template v-for="(_, slot) in $slots" #[slot]="scope">
            <slot :name="slot" v-bind="scope ?? {}" />
        </template>
    </DSSwitches>
    <DSNumber
        v-else-if="type === 'dsnumber'"
        :id="fieldId"
        :model-value="form.state[name]"
        :error="error"
        v-bind="$attrs"
        @update:model-value="commitValue"
    />
    <DSRangeSlider
        v-else-if="type === 'range'"
        :id="fieldId"
        :min-value="form.state[name] instanceof Array ? form.state[name][0] : form.state[name]"
        :max-value="form.state[name][1]"
        :error="error"
        v-bind="$attrs"
        @update:min-value="e => commitValue(form.state[name] instanceof Array ? [e, form.state[name][1]] : e)"
        @update:max-value="e => commitValue([form.state[name][0], e])"
    />
    <DSCalendar
        v-else-if="['calendar'].includes(type)"
        :id="fieldId"
        :model-value="form.state[name]"
        v-bind="$attrs"
        @update:model-value="commitValue"
    />
</template>
<script setup>
import { inject, computed, useId, useAttrs } from '#imports';
import { formInjectionKey } from '~dsc/utils/form';

const attrs = useAttrs();

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    type: {
        type: String,
        default: 'text',
    },
    id: {
        type: String,
        default: undefined,
    },
});

const form = inject(formInjectionKey);
const fieldState = computed(() => form.value.fieldState[props.name]);
const error = computed(() => fieldState.value.errors?.[0]);
const required = computed(() => attrs.required === undefined && form.value.rules?.[props.name]?.includes?.('notEmpty'));
const fieldId = props.id ? props.id : `${props.type}-${useId()}`;

if (!form) throw new Error('DSField must be used inside DSForm');
if (!fieldState.value) throw new Error(`Couldn't find field named ${props.name}`);

const commitValue = value => form.value.commitValue(props.name, value);
</script>
